// Category structure with support for nested subcategories and multilingual names
export interface Category {
  id: string;
  name: {
    de: string; // German is required and serves as fallback
    en?: string;
    [key: string]: string | undefined;
  };
  parent?: string; // Parent category ID
  subcategories?: Category[];
}

// Helper function to get category name in specific language with fallback to German
export const getCategoryName = (
  category: Category,
  language: string = "de"
): string => {
  return category.name[language] || category.name.de;
};

export const categoryStructure: Category[] = [
  {
    id: "wissenschaft",
    name: { de: "Wissenschaft & Forschung", en: "Science & Research" },
    subcategories: [
      {
        id: "wissenschaft-physik",
        name: { de: "Physik", en: "Physics" },
        parent: "wissenschaft",
        subcategories: [
          {
            id: "wissenschaft-physik-theoretisch",
            name: {
              de: "Theoretische Physik",
              en: "Theoretical Physics",
            },
            parent: "wissenschaft-physik",
          },
          {
            id: "wissenschaft-physik-experimentell",
            name: {
              de: "Experimentelle Physik",
              en: "Experimental Physics",
            },
            parent: "wissenschaft-physik",
          },
          {
            id: "wissenschaft-physik-astrophysik",
            name: { de: "Astrophysik", en: "Astrophysics" },
            parent: "wissenschaft-physik",
          },
        ],
      },
      {
        id: "wissenschaft-chemie",
        name: { de: "Chemie", en: "Chemistry" },
        parent: "wissenschaft",
        subcategories: [
          {
            id: "wissenschaft-chemie-organisch",
            name: {
              de: "Organische Chemie",
              en: "Organic Chemistry",
            },
            parent: "wissenschaft-chemie",
          },
          {
            id: "wissenschaft-chemie-anorganisch",
            name: {
              de: "Anorganische Chemie",
              en: "Inorganic Chemistry",
            },
            parent: "wissenschaft-chemie",
          },
        ],
      },
      {
        id: "wissenschaft-biologie",
        name: { de: "Biologie", en: "Biology" },
        parent: "wissenschaft",
        subcategories: [
          {
            id: "wissenschaft-biologie-molekular",
            name: {
              de: "Molekularbiologie",
              en: "Molecular Biology",
            },
            parent: "wissenschaft-biologie",
          },
          {
            id: "wissenschaft-biologie-oekologie",
            name: { de: "Ökologie", en: "Ecology" },
            parent: "wissenschaft-biologie",
          },
        ],
      },
      {
        id: "wissenschaft-forschung",
        name: { de: "Forschung", en: "Research" },
        parent: "wissenschaft",
      },
      {
        id: "wissenschaft-institute",
        name: { de: "Institute", en: "Institutes" },
        parent: "wissenschaft",
      },
    ],
  },
  {
    id: "bildung",
    name: { de: "Bildung & Kultur", en: "Education & Culture" },
    subcategories: [
      {
        id: "bildung-universitaeten",
        name: { de: "Universitäten", en: "Universities" },
        parent: "bildung",
        subcategories: [
          {
            id: "bildung-universitaeten-technisch",
            name: {
              de: "Technische Universitäten",
              en: "Technical Universities",
            },
            parent: "bildung-universitaeten",
          },
          {
            id: "bildung-universitaeten-volluniversitaeten",
            name: {
              de: "Volluniversitäten",
              en: "Comprehensive Universities",
            },
            parent: "bildung-universitaeten",
          },
        ],
      },
      {
        id: "bildung-bibliotheken",
        name: { de: "Bibliotheken", en: "Libraries" },
        parent: "bildung",
        subcategories: [
          {
            id: "bildung-bibliotheken-national",
            name: {
              de: "Nationalbibliotheken",
              en: "National Libraries",
            },
            parent: "bildung-bibliotheken",
          },
          {
            id: "bildung-bibliotheken-uni",
            name: {
              de: "Universitätsbibliotheken",
              en: "University Libraries",
            },
            parent: "bildung-bibliotheken",
          },
          {
            id: "bildung-bibliotheken-stadt",
            name: { de: "Stadtbibliotheken", en: "City Libraries" },
            parent: "bildung-bibliotheken",
          },
        ],
      },
      {
        id: "bildung-museen",
        name: { de: "Museen", en: "Museums" },
        parent: "bildung",
      },
      {
        id: "bildung-theater",
        name: { de: "Theater", en: "Theaters" },
        parent: "bildung",
      },
      {
        id: "bildung-schulen",
        name: { de: "Schulen", en: "Schools" },
        parent: "bildung",
      },
    ],
  },
  {
    id: "wirtschaft",
    name: { de: "Wirtschaft & Finanzen", en: "Economy & Finance" },
    subcategories: [
      {
        id: "wirtschaft-banken",
        name: { de: "Banken", en: "Banks" },
        parent: "wirtschaft",
        subcategories: [
          {
            id: "wirtschaft-banken-privat",
            name: { de: "Privatbanken", en: "Private Banks" },
            parent: "wirtschaft-banken",
          },
          {
            id: "wirtschaft-banken-genossenschaft",
            name: {
              de: "Genossenschaftsbanken",
              en: "Cooperative Banks",
            },
            parent: "wirtschaft-banken",
          },
          {
            id: "wirtschaft-banken-sparkassen",
            name: { de: "Sparkassen", en: "Savings Banks" },
            parent: "wirtschaft-banken",
          },
        ],
      },
      {
        id: "wirtschaft-versicherungen",
        name: { de: "Versicherungen", en: "Insurance" },
        parent: "wirtschaft",
      },
      {
        id: "wirtschaft-boerse",
        name: { de: "Börse", en: "Stock Exchange" },
        parent: "wirtschaft",
      },
      {
        id: "wirtschaft-unternehmen",
        name: { de: "Unternehmen", en: "Companies" },
        parent: "wirtschaft",
      },
    ],
  },
  {
    id: "gesundheit",
    name: { de: "Gesundheit & Medizin", en: "Health & Medicine" },
    subcategories: [
      {
        id: "gesundheit-krankenhaeuser",
        name: { de: "Krankenhäuser", en: "Hospitals" },
        parent: "gesundheit",
        subcategories: [
          {
            id: "gesundheit-krankenhaeuser-uni",
            name: {
              de: "Universitätskliniken",
              en: "University Hospitals",
            },
            parent: "gesundheit-krankenhaeuser",
          },
          {
            id: "gesundheit-krankenhaeuser-fach",
            name: { de: "Fachkliniken", en: "Specialized Clinics" },
            parent: "gesundheit-krankenhaeuser",
          },
        ],
      },
      {
        id: "gesundheit-apotheken",
        name: { de: "Apotheken", en: "Pharmacies" },
        parent: "gesundheit",
      },
      {
        id: "gesundheit-forschung",
        name: { de: "Medizinische Forschung", en: "Medical Research" },
        parent: "gesundheit",
      },
      {
        id: "gesundheit-praevention",
        name: { de: "Prävention", en: "Prevention" },
        parent: "gesundheit",
      },
    ],
  },
  {
    id: "oeffentlich",
    name: { de: "Öffentliche Einrichtungen", en: "Public Institutions" },
    subcategories: [
      {
        id: "oeffentlich-bund",
        name: { de: "Bundesbehörden", en: "Federal Authorities" },
        parent: "oeffentlich",
      },
      {
        id: "oeffentlich-land",
        name: { de: "Landesbehörden", en: "State Authorities" },
        parent: "oeffentlich",
      },
      {
        id: "oeffentlich-kommune",
        name: {
          de: "Kommunalverwaltung",
          en: "Municipal Administration",
        },
        parent: "oeffentlich",
      },
      {
        id: "oeffentlich-justiz",
        name: { de: "Justiz", en: "Justice" },
        parent: "oeffentlich",
      },
    ],
  },
];

// Helper function to get all categories as a flat list
export const getAllCategories = (): Category[] => {
  const flatten = (categories: Category[]): Category[] => {
    return categories.reduce((acc, cat) => {
      acc.push(cat);
      if (cat.subcategories) {
        acc.push(...flatten(cat.subcategories));
      }
      return acc;
    }, [] as Category[]);
  };
  return flatten(categoryStructure);
};

// Helper function to get a category by ID
export const getCategoryById = (id: string): Category | undefined => {
  return getAllCategories().find((cat) => cat.id === id);
};

// Helper function to get subcategories of a category
export const getSubcategories = (categoryId: string): Category[] => {
  const category = getCategoryById(categoryId);
  return category?.subcategories || [];
};

// Helper function to get category path (breadcrumb)
export const getCategoryPath = (categoryId: string): Category[] => {
  const allCategories = getAllCategories();
  const path: Category[] = [];

  let currentId: string | undefined = categoryId;
  while (currentId) {
    const category = allCategories.find((cat) => cat.id === currentId);
    if (category) {
      path.unshift(category);
      currentId = category.parent;
    } else {
      break;
    }
  }

  return path;
};
